import React from "react";
import { MapStateToPropsParam } from "react-redux";

import { Charts } from "@/mk2/apps/strollers/containers/Charts/Charts";
import { chartsLoad } from "@/mk2/apps/strollers/containers/Charts/Charts.actions";
import reducer from "@/mk2/apps/strollers/containers/Charts/Charts.reducers";
import saga from "@/mk2/apps/strollers/containers/Charts/Charts.sagas";
import { getChartsPerFilterState } from "@/mk2/apps/strollers/containers/Charts/Charts.selectors";
import {
    strollersComparisonLoad,
    strollersComparisonToggle,
} from "@/mk2/apps/strollers/containers/StrollersComparison/StrollersComparison.actions";
import strollersComparisonReducer from "@/mk2/apps/strollers/containers/StrollersComparison/StrollersComparison.reducers";
import strollersComparisonSaga from "@/mk2/apps/strollers/containers/StrollersComparison/StrollersComparison.sagas";
import { getStrollersComparisonState } from "@/mk2/apps/strollers/containers/StrollersComparison/StrollersComparison.selectors";
import { strollersUrl } from "@/mk2/apps/strollers/urls";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import Page, { HalfpageProps, PageRouteProps } from "@/mk2/pages/Page";
import { AppState, getRequestBaseUrl } from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import { StrollerEntity, StrollerSchema } from "@/mk2/schemas";
import { getDenormalizedEntities } from "@/mk2/selectors";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";
import { Store } from "@/mk2/store/configureStore";
import {
    SEO_DESCRIPTION_STROLLERS_CHARTS,
    SEO_TITLE_STROLLERS_CHARTS,
    STROLLERS_CHARTS_TITLE,
} from "@/mk/autogenerated/translation";

interface OwnProps {}

interface StateProps {
    strollersByUserScore: StrollerEntity[];
    strollersByProfileCount: StrollerEntity[];
    loadingState: LoadingState;
    errorMessage: string;
    comparison: number[];
    baseUrl: string;
}

interface DispatchProps {
    onLoad();
    onStrollersComparisonLoad();
    onStrollersComparisonToggle(id: number, location: HistoryLocationWithState);
}

type RouteProps = PageRouteProps<{}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class ChartsPage extends Page<OwnProps & RouteProps, StateProps, DispatchProps> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const chartsState = getChartsPerFilterState(state);
            const strollersComparisonState = getStrollersComparisonState(state);

            return {
                errorMessage: chartsState.errorMessage,
                loadingState: chartsState.loadingState,
                strollersByUserScore: getDenormalizedEntities<StrollerEntity>(
                    state,
                    StrollerSchema,
                    chartsState.strollersByUserScoreIds,
                ),
                strollersByProfileCount: getDenormalizedEntities<StrollerEntity>(
                    state,
                    StrollerSchema,
                    chartsState.strollersByProfileCountIds,
                ),
                comparison: strollersComparisonState.allIds,
                baseUrl: getRequestBaseUrl(state),
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: chartsLoad,
            onStrollersComparisonToggle: strollersComparisonToggle,
            onStrollersComparisonLoad: strollersComparisonLoad,
        };
    }

    public getMetaTitle(props: Props): string {
        return SEO_TITLE_STROLLERS_CHARTS;
    }

    public getMetaDescription(props: Props): string {
        return SEO_DESCRIPTION_STROLLERS_CHARTS;
    }

    public render(props: Props): false | JSX.Element {
        return <Charts {...props} />;
    }

    public getHeaderTitle(props: Props): string {
        return STROLLERS_CHARTS_TITLE;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        return strollersUrl;
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return {
            zone: "Strollers Index - Halfpage Sticky (Desktop)",
        };
    }

    public hasMobileLogo(props: Props): boolean {
        return true;
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.strollers.charts", reducer);
        store.injectSaga("containers.strollers.charts", saga);
        store.injectReducer("containers.strollers.strollersComparison", strollersComparisonReducer);
        store.injectSaga("containers.strollers.strollersComparison", strollersComparisonSaga);
    }
}
